/** @jsxImportSource @emotion/react */
import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { Alert, Select, Space, Spin } from 'antd'
import React, { useContext, useEffect, useState } from 'react'
import { Button as SNButton } from 'src/components/Button'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import { ScheduleReprocessingContext } from '../ScheduleReprocessingContext'

const NewDaySmartForm = ({
  daySmartImportModalRef,
  readItem,
  readyEntityCount,
  account,
  ORG_ID,
}) => {
  const [daySmartSeasons, setDaySmartSeasons] = useState([])
  const [selectedDaySmartSeason, setSelectedDaySmartSeason] = useState(null)
  const [isLoadingDaySmart, setIsLoadingDaySmart] = useState(false)
  const [daySmartError, setDaySmartError] = useState(null)
  const [isSyncingDaySmart, setIsSyncingDaySmart] = useState(false)
  const [daySmartSuccess, setDaySmartSuccess] = useState(false)
  // const { listeners, addPending } = useContext(ScheduleReprocessingContext)
  // const [isPending, setIsPending] = useState(
  //   !!listeners[selectedDaySmartSeason]
  // )
  // useEffect(() => {
  //   if (isPending && !listeners[selectedDaySmartSeason]) {
  //     // This means it finished the processing
  //     // onSuccess()
  //   }
  // }, [listeners[selectedDaySmartSeason]])

  const onSyncDaySmart = async () => {
    try {
      setIsSyncingDaySmart(true)
      await req(`/organizations/${ORG_ID}/daysmart-import-season`, {
        method: 'POST',
        body: JSON.stringify({
          season_id: selectedDaySmartSeason,
        }),
      })
      setDaySmartSuccess(true)
    } catch (e) {
      const errorMessage = getErrorMessage(e)
      setDaySmartError(errorMessage)
    } finally {
      setIsSyncingDaySmart(false)
    }
  }

  // Filter `option.label` match the user type `input`
  const filterOption = (input, option) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  useEffect(() => {
    const fetchDaySmartSeasons = async () => {
      try {
        setIsLoadingDaySmart(true)
        const { data } = await req(
          `/account/${account?.uid}/daysmart-seasons-search`
        )
        if (data) {
          setDaySmartSeasons(
            data?.seasons?.map((item) => {
              return {
                label: `${item?.name} (${item?.season_id})`,
                value: item?.season_id,
                ...item,
              }
            })
          )
        }
      } catch (e) {
        const errorMessage = getErrorMessage(e)
        setDaySmartError(errorMessage)
      } finally {
        setIsLoadingDaySmart(false)
      }
    }
    if (account?.uid) {
      fetchDaySmartSeasons()
    }
  }, [account?.uid])

  return (
    <div
      css={css`
        width: 45vw;
        background: #26303e;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        transition: all 0.1s ease-in-out;
        border-radius: 8px;
        padding-top: 16px;
      `}
    >
      <button
        onClick={() => {
          if (daySmartSuccess) {
            readItem(ENTITY_TYPES.org, ORG_ID)
            readyEntityCount()
            daySmartImportModalRef?.current?.closeModal()
            return
          }
          daySmartImportModalRef?.current?.closeModal()
        }}
        css={css`
          position: fixed;
          right: 40px;
          top: 40px;
          background: none;
          border: none;
        `}
      >
        <CloseOutlined
          fontSize={20}
          style={{
            color: colors.WHITE,
            fontSize: 20,
          }}
        />
      </button>
      <button
        onClick={() => {
          window.open(
            'https://sportninja.zendesk.com/hc/en-us/articles/25378814243988-DaySmart-Integration-NEW',
            '_blank'
          )
        }}
        css={css`
          position: fixed;
          right: 70px;
          top: 40px;
          background: none;
          border: none;
        `}
      >
        <QuestionCircleOutlined
          fontSize={20}
          style={{
            color: colors.WHITE,
            fontSize: 20,
          }}
        />
      </button>
      <div
        css={css`
          padding-left: 16px;
          padding-right: 16px;
          padding-bottom: 16px;
          display: flex;
          flex: 1;
          flex-direction: column;
          height: 100%;
          overflow-y: auto;
          max-height: 550px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            width: 100%;
            align-items: center;
          `}
        >
          <p
            css={css`
              font-size: 36px;
              font-family: ${isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'Rift, Arial, Helvetica, sans-serif'};
              font-weight: 700;
            `}
          >
            {'DaySmart Season Import'}
          </p>
        </div>
        <div
          css={css`
            border-bottom: 1px solid ${colors.SOFT_STEEL};
            margin-bottom: 16px;
            margin-top: 16px;
          `}
        />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            margin-bottom: 8px;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <span
              css={css`
                color: #fff;
                font-size: 14px;
                font-weight: 500;
                line-height: 145%; /* 20.3px */
              `}
            >
              DaySmart Season Info
            </span>
            <span
              css={css`
                color: var(--Foundation-Secondary-secondary-25, #a6aab0);
                font-size: 12px;
                font-weight: 400;
                line-height: 145%; /* 17.4px */
              `}
            >
              Search by name or season ID
            </span>
          </div>
        </div>
        <Select
          filterOption={filterOption}
          optionFilterProp='label'
          showSearch={true}
          onChange={(e) => {
            // addPending(e)
            setSelectedDaySmartSeason(e)
          }}
          options={daySmartSeasons}
          value={selectedDaySmartSeason}
          placeholder={'Select a DaySmart Season'}
          disabled={isLoadingDaySmart || isSyncingDaySmart || daySmartSuccess}
        />
        {selectedDaySmartSeason && !isSyncingDaySmart ? (
          <div
            css={css`
              margin-top: 16px;
              display: flex;
              flex-direction: column;
              padding: 16px;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
              border-radius: 8px;
              background: var(--Foundation-Secondary-secondary-400, #1b222b);
            `}
          >
            <span
              css={css`
                color: var(--Neutrals-White, #fff);
                font-size: 20px;
                font-weight: 600;
                line-height: 120%; /* 24px */
                border-bottom: 1px solid ${'#6A6C70'};
                width: 100%;
              `}
            >
              DaySmart Season
            </span>
            <div css={css``} />
            <div
              css={css`
                display: flex;
                flex-direction: row;
                gap: 8px;
              `}
            >
              <span
                css={css`
                  color: var(--Neutrals-neutrals-0, #fff);
                  font-size: 14px;
                  font-weight: 700;
                  line-height: 145%; /* 20.3px */
                `}
              >
                Name:
              </span>
              <span
                css={css`
                  color: var(--Neutrals-neutrals-0, #fff);
                  font-size: 14px;
                  font-weight: 400;
                  line-height: 145%; /* 20.3px */
                `}
              >
                {
                  daySmartSeasons.find(
                    (season) => season?.season_id === selectedDaySmartSeason
                  )?.name
                }
              </span>
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: row;
                gap: 8px;
              `}
            >
              <span
                css={css`
                  color: var(--Neutrals-neutrals-0, #fff);
                  font-size: 14px;
                  font-weight: 700;
                  line-height: 145%; /* 20.3px */
                `}
              >
                Start Date:
              </span>
              <span
                css={css`
                  color: var(--Neutrals-neutrals-0, #fff);
                  font-size: 14px;
                  font-weight: 400;
                  line-height: 145%; /* 20.3px */
                `}
              >
                {
                  daySmartSeasons
                    .find(
                      (season) => season?.season_id === selectedDaySmartSeason
                    )
                    ?.start_date.split(' ')[0]
                }
              </span>
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: row;
                gap: 8px;
              `}
            >
              <span
                css={css`
                  color: var(--Neutrals-neutrals-0, #fff);
                  font-size: 14px;
                  font-weight: 700;
                  line-height: 145%; /* 20.3px */
                `}
              >
                End Date:
              </span>
              <span
                css={css`
                  color: var(--Neutrals-neutrals-0, #fff);
                  font-size: 14px;
                  font-weight: 400;
                  line-height: 145%; /* 20.3px */
                `}
              >
                {
                  daySmartSeasons
                    .find(
                      (season) => season?.season_id === selectedDaySmartSeason
                    )
                    ?.end_date.split(' ')[0]
                }
              </span>
            </div>
          </div>
        ) : null}
        {isSyncingDaySmart ? (
          <div
            css={css`
              margin-top: 16px;
              display: flex;
              flex-direction: column;
              padding: 16px;
              align-items: center;
              gap: 8px;
            `}
          >
            <span
              css={css`
                color: var(--Neutrals-neutrals-0, #fff);
                text-align: center;
                font-size: 16px;
                font-weight: 500;
                line-height: 145%; /* 23.2px */
                margin-bottom: 8px;
              `}
            >
              Importing Season
            </span>
            <Spin tip='Loading' />
          </div>
        ) : null}
        <div
          css={css`
            margin-top: 20px;
            align-self: flex-end;
            width: 100%;
            position: sticky;
            bottom: 0;
            background-color: ${colors.HEADER};
            z-index: 100;
            padding-top: 16px;
          `}
        >
          {daySmartError ? (
            <Space
              direction='vertical'
              style={{ width: '100%', marginBottom: 16 }}
            >
              <Alert message={daySmartError} type='error' showIcon closable />
            </Space>
          ) : null}
          {daySmartSuccess ? (
            <Space
              direction='vertical'
              style={{ width: '100%', marginBottom: 16 }}
            >
              <Alert
                message={'Season imported successfully'}
                type='success'
                showIcon
                closable
              />
            </Space>
          ) : null}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              width: 100%;
            `}
          >
            <SNButton
              onClick={(e) => {
                e.preventDefault()
                daySmartImportModalRef?.current?.closeModal()
              }}
              label={'Cancel'}
              variant='secondary'
              disabled={
                isLoadingDaySmart || isSyncingDaySmart || daySmartSuccess
              }
            />
            <SNButton
              onClick={async () => {
                if (daySmartSuccess) {
                  readItem(ENTITY_TYPES.org, ORG_ID)
                  readyEntityCount()
                  daySmartImportModalRef?.current?.closeModal()
                  return
                }
                await onSyncDaySmart()
              }}
              type='submit'
              label={daySmartSuccess ? 'Ok' : 'Confirm'}
              isLoading={isLoadingDaySmart}
              disabled={
                isLoadingDaySmart ||
                !selectedDaySmartSeason ||
                isSyncingDaySmart
              }
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewDaySmartForm
